import { useReduxSelector } from "@hooks/useReduxSelector";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { hasDuplicateAttribute } from "@helpers/_functions";
import { setAdditionalInformation } from "@store/actions/settings/additionalInformationActions";
import { useDispatch } from "react-redux";
import { AdditionalInformationService } from "@services/all.services";
import { useNotification } from "@hooks/useNotification";
import InformationItem from "@entreprise/employeTab/container/employeeFilesNew/container/PersonnalInformations/InformationItem";

export const useSectionAdditionalInfoColumns = () => {
  const { i18n } = useTranslation("settings.additionalInfo");
  const addionalInfoList = useReduxSelector(({ settings }) => settings.additionalInformation?.list || []);

  return useMemo(
    () => addionalInfoList.map((item) => ({
      id: item._id,
      label: i18n.language === "fr" ? item.designationFr : item.designationEn
    })),
    [addionalInfoList, i18n.language]
  )
}

export const SectionAdditionalInfoTableBulkHeader = ({ hiddenColumns = {} }) => {
  const { i18n } = useTranslation("settings.additionalInfo");
  const addionalInfoList = useReduxSelector(({ settings }) => settings.additionalInformation?.list || []);

  return addionalInfoList?.map((item) => (
    <th
      key={item._id}
      className={`arh-w-72 arh-min-w-72 arh-max-w-72 arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[item._id] ? "arh-hidden" : ""}`}
    >
      {i18n.language === "fr" ? item.designationFr : item.designationEn}
    </th>
  ))
}

export const SectionAdditionalInfoTableBulkCells = ({
  control,
  employeeIndex,
  employee,
  readOnly = false,
  mustWait,
  hiddenColumns = {}
}) => {
  const { t } = useTranslation("settings.additionalInfo");
  const addionalInfoList = useReduxSelector(({ settings }) => settings.additionalInformation?.list || []);
  const dispatch = useDispatch();
  const { showError } = useNotification();

  const updateProcess = (dataToSave) => {
    AdditionalInformationService.updateById(dataToSave);
    dispatch(
      setAdditionalInformation({
        list: addionalInfoList?.map((item) => item._id === dataToSave._id ? dataToSave : item),
        isLoaded: true
      })
    );
  }

  const addItem = (data, index) => {
    const dataToSave = {
      ...addionalInfoList?.[index],
      infosList: [...addionalInfoList?.[index]?.infosList, { _id: data.name, name: data.name }]
    }
    if (hasDuplicateAttribute(dataToSave.infosList, "name")) {
      showError("already_exists", { object: "TheTitle" });
    } else {
      updateProcess(dataToSave);
    }
  }

  const editItem = (data, index) => {
    const dataToSave = {
      ...addionalInfoList?.[index],
      infosList: addionalInfoList?.[index]?.infosList?.map((item) => item._id === data._id ? { _id: data.name, name: data.name } : item)
    }
    if (hasDuplicateAttribute(dataToSave.infosList, "name")) {
      showError("already_exists", { object: "TheTitle" });
    } else {
      updateProcess(dataToSave);
    }
  }

  const deleteItem = (data, index) => {
    const dataToSave = {
      ...addionalInfoList?.[index],
      infosList: addionalInfoList?.[index]?.infosList?.filter((item) => item._id !== data._id)
    }
    updateProcess(dataToSave);
  }

  return (
    addionalInfoList?.map((item, index) => (
      <td
        className={`arh-w-72 arh-min-w-72 arh-max-w-72 arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[item._id] ? "arh-hidden" : ""}`}
        key={item._id}
      >
        <InformationItem
          item={item}
          index={index}
          control={control}
          fieldNamePrefix={`employees.${employeeIndex}.additionalInfo`}
          readOnly={mustWait || readOnly}
          addItem={addItem}
          deleteItem={deleteItem}
          editItem={editItem}
          translator={t}
          widthStyle="arh-w-64"
          verticalPaddingStyle="arh-py-1"
          defaultValue={employee.additionalInfo?.[item._id]}
          hideLabel
        />
      </td>
    ))
  )
}